const UNSUPPORTED_REGION_PATTERN = /unsupported region code/i;

const isUnsupportedRegionError = (errorData) => {
  if (!errorData) return false;
  if (typeof errorData === 'string') {
    return UNSUPPORTED_REGION_PATTERN.test(errorData);
  }

  const message = errorData?.error?.message || errorData?.message || '';
  if (UNSUPPORTED_REGION_PATTERN.test(message)) return true;

  try {
    return UNSUPPORTED_REGION_PATTERN.test(JSON.stringify(errorData));
  } catch {
    return false;
  }
};

const createUnsupportedRegionResult = (address) => ({
  isValid: true,
  message: 'Address validation is not available for this country. You may proceed.',
  skippedValidation: true,
  city: '',
  state: '',
  zip: '',
  formattedAddress: address,
});

const hasSuspiciousComponents = (components = []) =>
  components.some((component) => component.confirmationLevel === 'UNCONFIRMED_AND_SUSPICIOUS');

const hasMissingNonSubpremiseComponents = (missingComponentTypes = []) =>
  missingComponentTypes.length > 0 &&
  !(missingComponentTypes.length === 1 && missingComponentTypes[0] === 'subpremise');

const extractAddressFields = (result, fallbackAddress) => {
  const addressComponents = result?.address?.addressComponents || [];

  return {
    city:
      addressComponents.find((component) => component.componentType === 'locality')?.componentName
        ?.text || '',
    state:
      addressComponents.find(
        (component) => component.componentType === 'administrative_area_level_1'
      )?.componentName?.text || '',
    zip:
      addressComponents.find((component) => component.componentType === 'postal_code')
        ?.componentName?.text || '',
    formattedAddress: result?.address?.formattedAddress || fallbackAddress,
  };
};

export const evaluateAddressValidation = (result, regionCode) => {
  const verdict = result?.verdict || {};
  const address = result?.address || {};
  const uspsData = result?.uspsData || {};
  const {
    possibleNextAction,
    validationGranularity,
    hasSpellCorrectedComponents,
    hasReplacedComponents,
    hasInferredComponents,
  } = verdict;

  if (
    possibleNextAction === 'FIX' ||
    validationGranularity === 'OTHER' ||
    validationGranularity === 'ROUTE' ||
    hasMissingNonSubpremiseComponents(address.missingComponentTypes) ||
    hasSuspiciousComponents(address.addressComponents) ||
    (address.unresolvedTokens?.length ?? 0) > 0
  ) {
    return {
      isValid: false,
      message: 'Please enter a valid, complete address.',
    };
  }

  if (
    possibleNextAction === 'CONFIRM_ADD_SUBPREMISES' ||
    address.missingComponentTypes?.includes('subpremise') ||
    uspsData.dpvConfirmation === 'S'
  ) {
    return {
      isValid: false,
      message: 'Please add a unit or apartment number.',
    };
  }

  if (possibleNextAction === 'ACCEPT') {
    if (regionCode === 'US') {
      const dpvConfirmation = uspsData.dpvConfirmation;
      if (dpvConfirmation && dpvConfirmation !== 'Y') {
        return {
          isValid: false,
          message:
            dpvConfirmation === 'S'
              ? 'Please add a unit or apartment number.'
              : 'Please enter a valid US address.',
        };
      }
    }

    return {
      isValid: true,
      message: 'Address validated successfully',
    };
  }

  if (
    possibleNextAction === 'CONFIRM' ||
    validationGranularity === 'PREMISE_PROXIMITY' ||
    hasSpellCorrectedComponents ||
    hasReplacedComponents ||
    hasInferredComponents
  ) {
    return {
      isValid: false,
      message: 'Please verify and correct your address.',
    };
  }

  if (regionCode === 'US') {
    const dpvConfirmation = uspsData.dpvConfirmation;
    if (dpvConfirmation && dpvConfirmation !== 'Y') {
      return {
        isValid: false,
        message:
          dpvConfirmation === 'S'
            ? 'Please add a unit or apartment number.'
            : 'Please enter a valid US address.',
      };
    }
  }

  return {
    isValid: false,
    message: 'Please enter a valid, complete address.',
  };
};

export const validateAddressWithAPI = async (address, regionCode, apiKey) => {
  try {
    const response = await fetch(
      `https://addressvalidation.googleapis.com/v1:validateAddress?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: {
            regionCode,
            addressLines: [address],
          },
        }),
      }
    );

    const responseText = await response.text();

    if (!response.ok) {
      let errorData = null;
      try {
        errorData = responseText ? JSON.parse(responseText) : null;
      } catch {
        errorData = responseText;
      }

      if (isUnsupportedRegionError(errorData) || isUnsupportedRegionError(responseText)) {
        return createUnsupportedRegionResult(address);
      }

      throw new Error('Address validation failed');
    }

    const data = responseText ? JSON.parse(responseText) : {};
    const extractedData = extractAddressFields(data.result, address);
    const validation = evaluateAddressValidation(data.result, regionCode);

    return {
      ...validation,
      details: data.result,
      ...extractedData,
    };
  } catch (error) {
    if (isUnsupportedRegionError(error?.responseText)) {
      return createUnsupportedRegionResult(address);
    }

    return {
      isValid: false,
      message: 'Failed to validate address. Please try again.',
      error,
      city: '',
      state: '',
      zip: '',
    };
  }
};
